import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";

import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import ListPageHeader from "@/components/ListPageHeader/ListPageHeader.vue";

import { sellContractsController } from "@/controllers/sellContracts";
import { SellContract } from "@/models/SellContract";

import { JSConn } from "@/utilities/js-conn";
import { itemRouteBuilder, NamedRts } from "@/router";

import { genericSort, sortIcon } from "@/components/helpers";

import BarPercentageChart from "@/components/BarPercentageChart/BarPercentageChart.vue";

import { debounce } from "@/utilities/debounce";
import {
  arrowUpHandler,
  arrowDownHandler,
  filterOnInputHandler,
  filterItems,
} from "@/utilities/pickListHelpers";

export default defineComponent({
  name: "sellContracts",
  props: {
    useMode: {
      type: String,
      required: false,
    },
  },
  components: {
    LoadingAnimation,
    BarPercentageChart,
    ListPageHeader,
  },

  setup(props, ctx) {
    const $store = ctx.root.$store;
    const dataLoaded = ref(false);
    const loadingMessage = ref(
      "Please wait while the sell contracts are loaded...",
    );

    const firstInput = ref<null | HTMLInputElement>(null);

    const showMenu = ref(false);

    const toggleMenu = (newState: boolean) => {
      showMenu.value = newState;
      if (!newState) {
        ctx.root.$nextTick(() => {
          if (firstInput.value) {
            firstInput.value.focus();
          }
        });
      }
    };

    let currentStatuses = ["available"];
    const filterableKeys: Array<keyof SellContract> = [
      "label",
      "client_label",
      "commodity_label",
    ];

    const filter = ref("");

    const clearFilter = () => {
      filterOnInputHandler(
        apiSellContracts,
        sellContracts,

        "",
        filter,
        debouncedFilteredSellContracts,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const filterSellContracts = (filter: string, sc: SellContract[]) => {
      return filterItems(filter, filterableKeys, sc);
    };

    const debouncedFilteredSellContracts = debounce(filterSellContracts, 500);

    const sortKey = ref<keyof SellContract>("commodity_label");
    const sortAsc = ref(true);

    const apiSellContracts = computed(() => {
      return $store.getters.sellContracts as SellContract[];
    });

    const showRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.sellContractDetails, id);
    };

    const editRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.sellContractEditForm, id);
    };

    const sellContracts = ref<SellContract[]>([]);

    const localSortIcon = (lSortKey: keyof SellContract) => {
      return sortIcon(sortAsc, sortKey, lSortKey);
    };

    const getSellContractsByStatus = (statuses: string[]) => {
      currentStatuses = [...statuses];
      dataLoaded.value = false;
      const jsConn = new JSConn();
      jsConn.formParams = {
        statuses: statuses,
      };
      return sellContractsController.needSellContracts(jsConn).then(() => {
        sellContracts.value = [...apiSellContracts.value];
        // flip the sort, otherwise on load it would be off
        sortAsc.value = !sortAsc.value;
        updateSort(sortKey.value);
        dataLoaded.value = true;
        return Promise.resolve();
      });
    };

    const updateSort = (lSortKey: keyof SellContract) => {
      sellContracts.value = genericSort(
        [...sellContracts.value],
        sortAsc,
        sortKey,
        lSortKey,
      );
    };

    onMounted(() => {
      getSellContractsByStatus(currentStatuses).finally(() => {
        dataLoaded.value = true;
        if (props.useMode !== "pickerMode") {
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        }
      });
    });

    // this is for when the sell contracts are used as a picker
    // they make the arrow keys work correctly
    const selectedIndex = ref(-1);

    const onArrowDown = (evt: any) => {
      evt.preventDefault();
      arrowDownHandler(selectedIndex, sellContracts.value.length);
    };

    const onArrowUp = (evt: any) => {
      evt.preventDefault();
      arrowUpHandler(selectedIndex);
    };

    const filterOnInput = ({ target }: { target: HTMLInputElement }) => {
      filterOnInputHandler(
        apiSellContracts,
        sellContracts,

        target.value,
        filter,
        debouncedFilteredSellContracts,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const filterOnEnter = (evt: any) => {
      evt.preventDefault();
      let tmpResult = sellContracts.value[selectedIndex.value];

      if (!tmpResult) {
        tmpResult = sellContracts.value[0];
      }

      if (props.useMode === "pickerMode") {
        sendSellContract(tmpResult);
      } else {
        ctx.root.$router.push(showRouteBuilder(tmpResult.id));
      }
    };

    const sendSellContract = (il: SellContract) => {
      ctx.emit("sendSellContract", il);
    };

    return {
      dataLoaded,
      loadingMessage,
      sellContracts,

      filter,
      filterOnEnter,
      filterOnInput,
      clearFilter,

      getSellContractsByStatus,
      updateSort,
      localSortIcon,

      showRouteBuilder,
      editRouteBuilder,

      onArrowUp,
      onArrowDown,
      selectedIndex,
      sendSellContract,

      showMenu,
      firstInput,
      toggleMenu,
    };
  },
});
